import { cn } from "@-/com.npmjs.cn";
import { useWindowDimensions, View } from "@-/com.npmjs.react-native";
import { Group, Panel, Separator, useDefaultLayout } from "@-/com.npmjs.react-resizable-panels";
import { useHasHydrated } from "@-/dev.improve.haniverse.lib.hydration";
import type { MapWorkspaceProps } from "#map-workspace-props";

export function MapWorkspace(props: MapWorkspaceProps) {
  const hasHydrated = useHasHydrated();
  // The library reads localStorage. Keep server HTML and the first client render identical.
  return hasHydrated ? (
    <ResizableMapWorkspace {...props} />
  ) : (
    <View className="flex-1">{props.map}</View>
  );
}

/** Web details occupy their own viewport; resizing also remeasures and reframes the map. */
function ResizableMapWorkspace({
  controls,
  details,
  map,
  showDetails,
  topInset,
}: MapWorkspaceProps) {
  const { height, width } = useWindowDimensions();
  const orientation = width >= 768 && width > height ? "horizontal" : "vertical";
  const horizontal = orientation === "horizontal";
  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    id: `learn-map-${orientation}`,
    onlySaveAfterUserInteractions: true,
    panelIds: ["learn-map-canvas-panel", "learn-map-details-panel"],
  });

  return (
    <View className="min-h-0 flex-1" style={{ paddingTop: topInset }}>
      <Group
        className="size-full min-h-0 min-w-0"
        defaultLayout={defaultLayout}
        id="learn-map-workspace"
        key={orientation}
        onLayoutChanged={onLayoutChanged}
        orientation={orientation}
      >
        <Panel
          className="relative flex min-h-0 min-w-0 flex-col"
          id="learn-map-canvas-panel"
          minSize="20%"
        >
          {map}
          {controls}
        </Panel>
        {showDetails ? (
          <>
            <Separator
              aria-label="Resize map and missions panels"
              className={cn(
                "group flex items-center justify-center bg-border transition-colors hover:bg-primary/40 focus-visible:bg-primary/40 focus-visible:outline-none",
                horizontal ? "w-2 cursor-col-resize" : "h-2 cursor-row-resize",
              )}
              id="learn-map-panel-resize"
            >
              <div
                className={cn(
                  "pointer-events-none rounded-full bg-muted-foreground/50 group-hover:bg-foreground",
                  horizontal ? "h-10 w-1" : "h-1 w-10",
                )}
              />
            </Separator>
            <Panel
              className="relative flex min-h-0 min-w-0 flex-col bg-background"
              defaultSize={horizontal ? "38%" : "45%"}
              id="learn-map-details-panel"
              maxSize={horizontal ? "60%" : "75%"}
              minSize={horizontal ? "280px" : "25%"}
            >
              {details}
            </Panel>
          </>
        ) : null}
      </Group>
    </View>
  );
}
